import React, { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '../utils/dateUtils';
import { toast } from 'sonner';
import {
  Bell, Mail, MessageCircle, Phone, Save, RefreshCw,
  TestTube2, Check, X, Clock, Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminNotificationsConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('config');

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, logsRes] = await Promise.all([
        fetch(`${BACKEND}/api/admin/notifications/config`, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/notifications/logs`, { headers: H }).then(r => r.json()),
      ]);
      setConfig(cfgRes);
      setLogs(logsRes.logs || []);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/notifications/config`, {
        method: 'PUT', headers: H, body: JSON.stringify(config)
      });
      if (!res.ok) throw new Error();
      toast.success('Configuration sauvegardée !');
      load();
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  }

  async function testNotif() {
    setTesting(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/notifications/test`, { method: 'POST', headers: H, body: '{}' });
      const d = await res.json();
      toast.success(d.message || 'Notification de test envoyée !');
      setTimeout(load, 2000);
    } catch { toast.error('Erreur test'); }
    setTesting(false);
  }

  const setF = (k, v) => setConfig(p => ({ ...p, [k]: v }));

  const TABS = [
    { id: 'config', label: 'Configuration', icon: <Bell size={14}/> },
    { id: 'logs', label: `Historique (${logs.length})`, icon: <Clock size={14}/> },
  ];

  const EVENT_LABELS = {
    nouveau_devis: '📋 Nouveau devis',
    devis_signe: '✍️ Devis signé',
    devis_paye: '💰 Acompte reçu',
    nouvelle_commande: '🛒 Commande boutique',
    nouveau_contact: '📩 Nouveau contact',
  };

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paramètres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Notifications</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Email, SMS et WhatsApp</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div className="flex items-center justify-end flex-wrap gap-3">
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1"/> Actualiser
          </Button>
          <Button onClick={testNotif} disabled={testing} size="sm"
            style={{ background: 'rgba(125,177,255,0.15)', color: 'var(--admin-blue)', border: '1px solid rgba(125,177,255,0.3)' }}>
            <TestTube2 size={14} className="mr-1"/>
            {testing ? 'Envoi...' : 'Tester'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
            style={activeTab === tab.id ? { background: '#fff', color: 'var(--admin-teal)' } : { color: 'var(--admin-muted)' }}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'config' && config && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Quand notifier */}
          <div className="p-5 rounded-2xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
            <h3 className="font-bold text-sm mb-4 flex items-center gap-2" style={{ color: 'var(--admin-text)' }}>
              <Bell size={14} style={{ color: 'var(--admin-teal)' }}/> Événements notifiés
            </h3>
            <p className="text-xs mb-3" style={{ color: 'var(--admin-muted)' }}>
              Vous serez notifié automatiquement lors de :
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(EVENT_LABELS).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 p-2.5 rounded-xl"
                  style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.15)' }}>
                  <Check size={12} style={{ color: 'var(--admin-teal)' }}/>
                  <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Email */}
          <div className="p-5 rounded-2xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${config.email_enabled ? 'rgba(31,157,122,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm flex items-center gap-2" style={{ color: 'var(--admin-text)' }}>
                <Mail size={14} style={{ color: 'var(--admin-green)' }}/> Email
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.email_enabled || false}
                  onChange={e => setF('email_enabled', e.target.checked)}
                  style={{ accentColor: '#1F9D7A' }}/>
                <span className="text-xs" style={{ color: config.email_enabled ? '#1F9D7A' : 'rgba(255,255,255,0.4)' }}>
                  {config.email_enabled ? 'Activé' : 'Désactivé'}
                </span>
              </label>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Email destinataire</label>
              <Input value={config.email_admin || ''} onChange={e => setF('email_admin', e.target.value)}
                placeholder="votre@email.fr" type="email"
                style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}/>
              <p className="text-xs mt-1" style={{ color: 'var(--admin-faint)' }}>Email envoyé via Brevo — déjà configuré ✅</p>
            </div>
          </div>

          {/* WhatsApp */}
          <div className="p-5 rounded-2xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${config.whatsapp_enabled ? 'rgba(37,211,102,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm flex items-center gap-2" style={{ color: 'var(--admin-text)' }}>
                <MessageCircle size={14} style={{ color: '#25D366' }}/> WhatsApp
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.whatsapp_enabled || false}
                  onChange={e => setF('whatsapp_enabled', e.target.checked)}
                  style={{ accentColor: '#25D366' }}/>
                <span className="text-xs" style={{ color: config.whatsapp_enabled ? '#25D366' : 'rgba(255,255,255,0.4)' }}>
                  {config.whatsapp_enabled ? 'Activé' : 'Désactivé'}
                </span>
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Numéro WhatsApp (format +33...)</label>
                <Input value={config.whatsapp_phone || ''} onChange={e => setF('whatsapp_phone', e.target.value)}
                  placeholder="+33612345678" type="tel"
                  style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}/>
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Clé API CallMeBot</label>
                <Input value={config.whatsapp_api_key || ''} onChange={e => setF('whatsapp_api_key', e.target.value)}
                  placeholder="Votre clé CallMeBot"
                  style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}/>
              </div>
              <div className="p-3 rounded-xl" style={{ background: 'rgba(37,211,102,0.06)', border: '1px solid rgba(37,211,102,0.15)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: '#25D366' }}>Comment obtenir votre clé CallMeBot (gratuit) :</p>
                <ol className="text-xs space-y-1" style={{ color: 'var(--admin-muted)', paddingLeft: 14 }}>
                  <li>1. Envoyez sur WhatsApp au <strong style={{ color: 'var(--admin-text)' }}>+34 644 52 74 59</strong> : <code style={{ color: '#25D366' }}>I allow callmebot to send me messages</code></li>
                  <li>2. Vous recevez votre clé API en réponse</li>
                  <li>3. Collez-la ci-dessus et activez</li>
                </ol>
              </div>
            </div>
          </div>

          {/* SMS */}
          <div className="p-5 rounded-2xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${config.sms_enabled ? 'rgba(125,177,255,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm flex items-center gap-2" style={{ color: 'var(--admin-text)' }}>
                <Smartphone size={14} style={{ color: 'var(--admin-blue)' }}/> SMS
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.sms_enabled || false}
                  onChange={e => setF('sms_enabled', e.target.checked)}
                  style={{ accentColor: '#7DB1FF' }}/>
                <span className="text-xs" style={{ color: config.sms_enabled ? '#7DB1FF' : 'rgba(255,255,255,0.4)' }}>
                  {config.sms_enabled ? 'Activé' : 'Désactivé'}
                </span>
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Numéro de téléphone (format +33...)</label>
                <Input value={config.sms_phone || ''} onChange={e => setF('sms_phone', e.target.value)}
                  placeholder="+33612345678" type="tel"
                  style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}/>
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Clé API TextBelt (optionnel)</label>
                <Input value={config.sms_api_key || ''} onChange={e => setF('sms_api_key', e.target.value)}
                  placeholder="textbelt (1 SMS/jour gratuit) ou votre clé"
                  style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}/>
                <p className="text-xs mt-1" style={{ color: 'var(--admin-faint)' }}>
                  Laissez vide pour 1 SMS/jour gratuit · Achetez des crédits sur textbelt.com
                </p>
              </div>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="w-full"
            style={{ background: 'rgba(93,188,191,0.2)', color: 'var(--admin-teal)', border: '1px solid var(--admin-teal-line)' }}>
            <Save size={14} className="mr-2"/>
            {saving ? 'Sauvegarde...' : 'Enregistrer la configuration'}
          </Button>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-2">
          {logs.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'var(--admin-faint)' }}>
              <Bell size={36} className="mx-auto mb-3 opacity-20"/>
              <p>Aucune notification envoyée pour le moment</p>
            </div>
          ) : logs.map((log, i) => (
            <div key={i} className="flex items-center gap-4 p-3 rounded-xl"
              style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <span className="text-lg">{EVENT_LABELS[log.event_type]?.slice(0,2) || '🔔'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>
                  {EVENT_LABELS[log.event_type] || log.event_type}
                </p>
                <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                  {log.data_summary?.client_nom} · {log.data_summary?.client_email}
                  {log.data_summary?.total ? ` · ${log.data_summary.total}€` : ''}
                </p>
              </div>
              <p className="text-xs flex-shrink-0" style={{ color: 'var(--admin-faint)' }}>
                {formatDateTime(log.sent_at)}
              </p>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
